import React, { useReducer, useContext, useEffect } from 'react';
import { ICreateSubCategoryValidationError, IServerError, TFileId, TLanguageIDs, IDefaultLanguage } from 'types';
import { createSubcategoryModalReducer } from './createSubcategoryModalReducer';
import { CreateSubcategoryModalActionTypes } from '../createSubcategoryModalActions';
import {
	createSubcategoryModalInitialState,
	CreateSubcategoryModalContext,
	TVisibleForm,
	TSelectedItem,
	TTypeOfModalWindow,
} from './createSubcategoryModalContext';
import { IDishAssignmentContext, DishAssignmentContext } from '../../../../dishAssignmentState/dishAssignmentContext';

const CreateSubcategoryModalState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createSubcategoryModalReducer, createSubcategoryModalInitialState);
	const { isShowModalWindowToCreateSubcategory, IDSelectedCustomer, IDSelectedRestaurant } =
		useContext<IDishAssignmentContext>(DishAssignmentContext);

	const changeVisibleForm = (value: TVisibleForm) => {
		dispatch({
			type: CreateSubcategoryModalActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	};

	const setFileId = (fileId: TFileId) => {
		dispatch({
			type: CreateSubcategoryModalActionTypes.SET_FILE_ID,
			payload: {
				fileId: fileId,
			},
		});
	};

	const changeNameOfSubCategory = (value: string) => {
		dispatch({
			type: CreateSubcategoryModalActionTypes.CHANGE_NAME_OF_SUBCATEGORY,
			payload: {
				nameOfSubCategory: value,
			},
		});
	};

	const setIDSelectedCategory = (value: TSelectedItem) => {
		dispatch({
			type: CreateSubcategoryModalActionTypes.SET_ID_SELECTED_CATEGORY,
			payload: {
				IDSelectedCategory: value,
			},
		});
	};

	const changeSubCategoryVisibilityStatus = (value: boolean) => {
		dispatch({
			type: CreateSubcategoryModalActionTypes.CHANGE_SUBCATEGORY_VISIBILITY_STATUS,
			payload: {
				isHiddenSubCategory: value,
			},
		});
	};

	const changeAvailableForRepetition = (value: boolean) => {
		dispatch({
			type: CreateSubcategoryModalActionTypes.CHANGE_AVAILABLE_FOR_REPETITION,
			payload: {
				isAvailableForRepetition: value,
			},
		});
	};

	const setTranslationTab = (value: Nullable<TLanguageIDs>) => {
		dispatch({
			type: CreateSubcategoryModalActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	};

	const setFetchedTranslations = (value: TLanguageIDs[], defaultCustomerLanguage: Nullable<IDefaultLanguage>) => {
		const translations = value.map((id) => ({ languageId: id, name: '' }));
		dispatch({
			type: CreateSubcategoryModalActionTypes.SET_FETCHED_TRANSLATIONS,
			payload: {
				translations,
				defaultCustomerLanguage,
			},
		});
	};

	const changeNameTranslation = (languageId: TLanguageIDs, value: string) => {
		const updatedTranslations = state.translations.map((translation) =>
			translation.languageId === languageId
				? {
						...translation,
						name: value,
				  }
				: translation,
		);
		dispatch({
			type: CreateSubcategoryModalActionTypes.CHANGE_TRANSLATION,
			payload: {
				translations: updatedTranslations,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: CreateSubcategoryModalActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const showErrorModalWithInfo = (value: IServerError<ICreateSubCategoryValidationError> | null) => {
		dispatch({
			type: CreateSubcategoryModalActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	};

	useEffect(() => {
		if (isShowModalWindowToCreateSubcategory) {
			dispatch({
				type: CreateSubcategoryModalActionTypes.INITIAL_DATA,
				payload: {
					IDCustomer: IDSelectedCustomer,
					IDRestaurant: IDSelectedRestaurant,
				},
			});
		}
	}, [isShowModalWindowToCreateSubcategory]);

	return (
		<CreateSubcategoryModalContext.Provider
			value={{
				changeVisibleForm,
				setFileId,
				changeNameOfSubCategory,
				setIDSelectedCategory,
				changeSubCategoryVisibilityStatus,
				changeAvailableForRepetition,
				setTranslationTab,
				setFetchedTranslations,
				changeNameTranslation,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				visibleForm: state.visibleForm,
				fileId: state.fileId,
				IDCustomer: state.IDCustomer,
				IDRestaurant: state.IDRestaurant,
				nameOfSubCategory: state.nameOfSubCategory,
				IDSelectedCategory: state.IDSelectedCategory,
				isHiddenSubCategory: state.isHiddenSubCategory,
				isAvailableForRepetition: state.isAvailableForRepetition,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</CreateSubcategoryModalContext.Provider>
	);
};

export default CreateSubcategoryModalState;
